/* MUI */
import { Box, Flex } from '@auviknetworks/iceberg-ui';

/* Shared */
import { Storage } from 'application/Shared/Handlers';
import { Route } from 'application/Shared/Helpers';

/* Yarn */
import { motion } from 'framer-motion';

/* React */
import React from 'react';
import { useParams } from 'react-router';

import { ExitSetupGuideLink } from '../internal/ExitSetupGuideLink';
import { DeployAgentChecklist } from './internal/DeployAgentChecklist';
import { QuickScanChecklist } from './internal/QuickScanChecklist';
import { SuccessGraphic } from './internal/SuccessGraphic';
import { WhatIsIncludedInTrialGraphic } from './internal/WhatIsIncludedInTrialGraphic';
import { WhyInstallAgentGraphic } from './internal/WhyInstallAgentGraphic';

export const MotionWrapper = ({ children }) => {
    const variants = {
        hide: {
            opacity: 0,
            transition: {
                delay: 0.5,
                duration: 1,
                when: 'afterChildren'
            }
        },
        show: {
            opacity: 1,
            transition: {
                delay: 0,
                duration: 1,
                when: 'beforeChildren'
            }
        }
    };

    return (
        <motion.div
            variants={variants}
            initial="hide"
            animate="show"
            style={{
                display: 'flex',
                height: '100%',
                justifyContent: 'center',
                width: '100%'
            }}
        >
            {children}
        </motion.div>
    );
};

const graphicsMap = ({
    currentSelection,
    currentStep,
    currentType,
    stepNumberMap
}) => {
    const map = {
        initial: {
            chooseOption: {
                component: (
                    <WhatIsIncludedInTrialGraphic
                        MotionWrapper={MotionWrapper}
                    />
                ),
                exitSetupGuideLink: '/dashboard'
            }
        },
        quickScan: {
            select: {
                component: (
                    <QuickScanChecklist
                        MotionWrapper={MotionWrapper}
                        stepNumberMap={stepNumberMap}
                    />
                ),
                exitSetupGuideLink: '/dashboard'
            },
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            configureAD: {
                component: (
                    <QuickScanChecklist
                        MotionWrapper={MotionWrapper}
                        stepNumberMap={stepNumberMap}
                    />
                ),
                exitSetupGuideLink: '/dashboard'
            },
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            configureGoogle: {
                component: (
                    <QuickScanChecklist
                        MotionWrapper={MotionWrapper}
                        stepNumberMap={stepNumberMap}
                    />
                ),
                exitSetupGuideLink: '/dashboard'
            },
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            chooseGroups: {
                component: (
                    <QuickScanChecklist
                        MotionWrapper={MotionWrapper}
                        stepNumberMap={stepNumberMap}
                    />
                ),
                exitSetupGuideLink: '/dashboard'
            },
            searchingApps: {
                component: (
                    <QuickScanChecklist
                        MotionWrapper={MotionWrapper}
                        stepNumberMap={stepNumberMap}
                    />
                ),
                exitSetupGuideLink: '/dashboard'
            },
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            completed: {
                component:
                    currentSelection === 'success' ? (
                        <SuccessGraphic
                            MotionWrapper={MotionWrapper}
                            stepNumberMap={stepNumberMap}
                        />
                    ) : (
                        <WhyInstallAgentGraphic
                            MotionWrapper={MotionWrapper}
                            stepNumberMap={stepNumberMap}
                        />
                    )
            }
        },
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        deployAgent: {
            select: {
                component: (
                    <DeployAgentChecklist
                        MotionWrapper={MotionWrapper}
                        stepNumberMap={stepNumberMap}
                    />
                ),
                exitSetupGuideLink: '/integrations/partnerLevel'
            },
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            configure: {
                component: (
                    <DeployAgentChecklist
                        MotionWrapper={MotionWrapper}
                        stepNumberMap={stepNumberMap}
                    />
                ),
                exitSetupGuideLink: '/integrations/partnerLevel'
            },
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            completed: {
                component: (
                    <SuccessGraphic
                        MotionWrapper={MotionWrapper}
                        stepNumberMap={stepNumberMap}
                    />
                )
            }
        }
    };
    return map[currentType][currentStep];
};

export const OnboardingGraphic = ({ stepNumberMap }) => {
    const {
        partnerId,
        currentSelection,
        currentStep = 'chooseOption',
        currentType = 'initial'
    } = useParams();
    const { component, exitSetupGuideLink } = graphicsMap({
        currentSelection,
        currentStep,
        currentType,
        stepNumberMap
    });

    const basePartnerRoute = Route.buildBasePartnerRoute({ partnerId });
    const link = `${basePartnerRoute}${exitSetupGuideLink}`;

    const setExitOnboarding = () => {
        // const sevenDays = 7 * 24 * 60 * 60 * 1000 = 604,800,000 milliseconds
        Storage.setWithExpiry('exitedOnboarding', true, 604800000);
    };

    return (
        <Flex
            alignItems="center"
            data-testid={`onboardingGraphic-${currentStep}`}
            flexDirection="column"
            height="100%"
            justifyContent="center"
            position="relative"
        >
            {component}
            {exitSetupGuideLink && (
                <Box position="absolute" bottom={0}>
                    <ExitSetupGuideLink
                        data-testid={`${currentStep}ExitSetupGuideLinkOnboardingGraphic`}
                        data-trackid={`${currentStep}ExitSetupGuideLinkOnboardingGraphic`}
                        link={link}
                        onClick={setExitOnboarding}
                    />
                </Box>
            )}
        </Flex>
    );
};
